import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';
import { ICON_MAP } from 'src/utils/constants';
import { GeneralASError } from 'src/registry/types/axios';
import { cn } from 'src/utils';
import { Unit } from 'src/features/units/utils/types';
import { unitFormSchema, UnitFormSchemaValues } from 'src/features/units/utils/formSchema';
import UnitForm from 'src/features/units/components/forms/UnitForm';
import { useGetUnit } from 'src/features/units/api/useGetUnit';

interface UnitFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedUnit?: Unit;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (data: UnitFormSchemaValues, onSuccess: () => void, onError: (error: GeneralASError) => void) => void;
}

function UnitFormModal<TData>({
    selectedUnit,
    isPending,
    children,
    className,
    icon,
    title,
    subtitle,
    mutate,
}: UnitFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const { data: unitData } = useGetUnit({
        unitId: selectedUnit?.id ?? '',
        config: { enabled: !!selectedUnit?.id && isModalOpen },
    });
    const onError = useOnError();

    const defaultValues = React.useMemo(() => {
        return {
            name: unitData?.name ?? undefined,
            code: unitData?.code ?? undefined,
            abbreviation: unitData?.abbreviation ?? undefined,
        };
    }, [unitData]);

    const form = useForm<UnitFormSchemaValues>({
        resolver: zodResolver(unitFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [form, isModalOpen, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: UnitFormSchemaValues) => {
            mutate(
                values,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },
        [mutate, onError, form.setError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <UnitForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedUnit && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default UnitFormModal;
